/* eslint-disable react/prop-types */
import React from 'react';
import { returnStatusColor } from './returnStatusColor';
import { format, parseISO } from 'date-fns';
import { getTimeUntil } from '../../utils/dateHelpers';
import { StatusPill } from '../ui/Status';
import Status from '../ui/Status';
import EventContentSkeleton from './EventContentSkeleton';
import usePostErrorStore from '../../store/usePostErrorStore';
import { AlertCircle, AlertTriangle, Loader2, ImageIcon, BookImage } from 'lucide-react';

/* ── Tiny type badge ── */
const TypeBadge = ({ isStory, className = '' }) => (
  <span
    className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none ${className}`}
    style={{
      background: isStory ? 'rgba(139,92,246,0.18)' : 'rgba(45,212,191,0.18)',
      color: isStory ? '#a78bfa' : 'var(--brand)',
      border: `1px solid ${isStory ? 'rgba(139,92,246,0.35)' : 'rgba(45,212,191,0.35)'}`,
    }}
  >
    {isStory
      ? <BookImage className='w-2.5 h-2.5 flex-shrink-0' />
      : <ImageIcon className='w-2.5 h-2.5 flex-shrink-0' />}
    <span className='truncate'>{isStory ? 'Story' : 'Post'}</span>
  </span>
);

const EventContent = ({ eventInfo }) => {
  const { title, extendedProps } = eventInfo.event;
  const { imageUrl, status, postId, caption, post } = extendedProps;
  const postError = usePostErrorStore((state) => state.postErrors[postId]);

  const view = eventInfo.view.type;
  const { borderColor, backgroundColor } = returnStatusColor(status);

  // Parse scheduledAt as local time (same as formatDateHelper) so all views agree
  const scheduledDate = post?.scheduledAt ? parseISO(post.scheduledAt) : null;
  const formattedTime = scheduledDate ? format(scheduledDate, 'HH:mm') : '';
  const isStory = title === 'Story';
  const isLoading = !imageUrl || status === 'draft';

  /* ══════════════════════════════════════════════════════
     MONTH GRID VIEW  (dayGridMonth)
  ══════════════════════════════════════════════════════ */
  if (view === 'dayGridMonth') {
    if (isLoading) return <EventContentSkeleton title={title} status={status} />;

    if (postError) {
      return (
        <div
          className='w-full h-full flex items-center gap-1 px-1.5 py-1 overflow-hidden rounded'
          style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.4)' }}
          title={postError}
        >
          <AlertTriangle className='w-3 h-3 flex-shrink-0' style={{ color: '#ca8a04' }} />
          <span className='text-[10px] truncate' style={{ color: '#ca8a04' }}>{postError}</span>
        </div>
      );
    }

    return (
      <>
        <div
          className='desktop-tour-calendar-event mobile-tour-calendar-event relative flex items-center gap-1.5 px-1.5 py-1 overflow-hidden rounded cursor-pointer w-full'
          style={{
            background: backgroundColor,
            borderLeft: `3px solid ${borderColor}`,
          }}
          title={`${status.charAt(0).toUpperCase() + status.slice(1)} · ${isStory ? 'Story' : 'Post'} · ${formattedTime}`}
        >
          {/* Thumbnail */}
          <img
            src={imageUrl}
            alt={isStory ? 'Story' : 'Post'}
            className='rounded object-cover flex-shrink-0'
            style={{
              width: isStory ? '18px' : '22px',
              height: isStory ? '30px' : '22px',
            }}
          />

          {/* Right side */}
          <div className='flex flex-col min-w-0 gap-0.5 flex-1'>
            <TypeBadge isStory={isStory} />
            <span
              className='text-[9px] leading-none font-medium'
              style={{ color: 'var(--text-muted)' }}
            >
              {formattedTime}
            </span>
          </div>

          {/* Far-right status dot */}
          <StatusPill dotColor={returnStatusColor(status).borderColor} status={status} />

          {/* Draft generating overlay */}
          {status === 'draft' && (
            <div
              className='absolute inset-0 flex items-center justify-center backdrop-blur-sm rounded'
              style={{ background: 'rgba(0,0,0,0.25)', zIndex: 1 }}
            >
              <Loader2 className='w-3 h-3 animate-spin' style={{ color: 'var(--brand)' }} />
            </div>
          )}
        </div>

      </>
    );
  }

  /* ══════════════════════════════════════════════════════
     LIST / DAY VIEW  (listWeek, timeGrid1Day)
  ══════════════════════════════════════════════════════ */
  if (view === 'listWeek' || view === 'timeGrid1Day') {
    if (isLoading && !postError) return <EventContentSkeleton title={title} status={status} />;

    if (postError) {
      return (
        <div
          className='relative flex items-center gap-3 p-3 rounded w-full'
          style={{ background: 'rgba(234,179,8,0.10)', border: '1px solid rgba(234,179,8,0.35)' }}
        >
          <AlertTriangle className='w-4 h-4 flex-shrink-0' style={{ color: '#ca8a04' }} />
          <span className='text-sm truncate' style={{ color: '#ca8a04' }}>{postError}</span>
        </div>
      );
    }

    return (
      <div
        className='desktop-tour-calendar-event mobile-tour-calendar-event relative flex items-center gap-4 p-3 cursor-pointer rounded w-full'
        style={{
          background: backgroundColor,
          border: `1px solid ${borderColor}`,
        }}
        title={`${status && status.charAt(0).toUpperCase() + status.slice(1)}`}
      >
        {/* Left: meta */}
        <div className='flex flex-col gap-1.5 min-w-0 flex-1'>
          <Status status={status} />
          <TypeBadge isStory={isStory} className='max-w-[72px]' />
          {caption && !isStory && (
            <p
              className='text-xs leading-relaxed line-clamp-3 mt-0.5'
              style={{ color: 'var(--text-muted)' }}
            >
              {caption}
            </p>
          )}
          <span className='text-xs font-medium' style={{ color: 'var(--text-body)' }}>
            {formattedTime} <span style={{ color: 'var(--text-muted)' }}>· {getTimeUntil(scheduledDate)}</span>
          </span>
        </div>

        {/* Right: thumbnail */}
        <img
          src={imageUrl}
          alt={isStory ? 'Story' : 'Post'}
          className='rounded-lg object-cover shadow-sm flex-shrink-0'
          style={{
            width: isStory ? '72px' : '104px',
            height: isStory ? '128px' : '104px',
          }}
        />
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════
     DEFAULT  (timeGridWeek etc.)
  ══════════════════════════════════════════════════════ */
  if (isLoading && !postError) return <EventContentSkeleton view={view} title={title} status={status} />;

  if (postError) {
    return (
      <div
        className='relative flex items-center overflow-hidden p-1 rounded'
        style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)' }}
      >
        <AlertCircle className='w-3.5 h-3.5 flex-shrink-0 mr-1' style={{ color: '#ef4444' }} />
        <span className='text-[10px] truncate' style={{ color: '#ef4444' }}>Error</span>
      </div>
    );
  }

  return (
    <>
      <div
        className='desktop-tour-calendar-event mobile-tour-calendar-event relative flex items-center overflow-hidden p-1 cursor-pointer rounded'
        title={`${status && status.charAt(0).toUpperCase() + status.slice(1)}`}
        style={{
          background: backgroundColor,
          borderLeft: `3px solid ${borderColor}`,
        }}
      >
        <div className='flex items-center gap-1.5 flex-1 min-w-0'>
          <img
            src={imageUrl}
            alt={isStory ? 'Story' : 'Post'}
            className='rounded object-cover flex-shrink-0'
            style={{
              width: isStory ? '14px' : '18px',
              height: isStory ? '24px' : '18px',
            }}
          />
          <div className='flex flex-col min-w-0 gap-0.5 flex-1'>
            <TypeBadge isStory={isStory} />
            <span className='text-[9px] leading-none' style={{ color: 'var(--text-muted)' }}>
              {eventInfo.timeText}
            </span>
          </div>
          {/* Far-right status dot */}
          <StatusPill dotColor={returnStatusColor(status).borderColor} status={status} />
        </div>
      </div>
    </>
  );
};

export default EventContent;
